import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { getLastScope, putLastScope } from "../api/profile";
import { navTo } from "../components/navTargets";
import { useContextChain } from "./ContextChain";

const LAST_PROJECT_VERSION = 1;
// Keep the JSON value comfortably below the profile API's 1,024-character limit.
const MAX_REMEMBERED_WORKSPACES = 8;

interface LastProjectEntry {
  workspaceId: string;
  projectId: string;
}

interface LastProjectState {
  version: typeof LAST_PROJECT_VERSION;
  projects: LastProjectEntry[];
}

export function parseLastProjects(value: string | null): LastProjectEntry[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as Partial<LastProjectState>;
    if (parsed.version !== LAST_PROJECT_VERSION || !Array.isArray(parsed.projects)) return [];
    return parsed.projects.filter(
      (entry): entry is LastProjectEntry => Boolean(
        entry
        && typeof entry.workspaceId === "string"
        && entry.workspaceId
        && typeof entry.projectId === "string"
        && entry.projectId,
      ),
    ).slice(0, MAX_REMEMBERED_WORKSPACES);
  } catch {
    return [];
  }
}

export function rememberProject(
  entries: LastProjectEntry[],
  workspaceId: string,
  projectId: string,
): LastProjectEntry[] {
  return [
    { workspaceId, projectId },
    ...entries.filter((entry) => entry.workspaceId !== workspaceId),
  ].slice(0, MAX_REMEMBERED_WORKSPACES);
}

function serializeLastProjects(entries: LastProjectEntry[]): string {
  return JSON.stringify({ version: LAST_PROJECT_VERSION, projects: entries });
}

/** Restores only the initial workspace-home visit; later Home clicks remain usable. */
export function LastProjectBridge(): null {
  const params = useParams({ strict: false }) as {
    ws?: string;
    client?: string;
    eng?: string;
    project?: string;
    node?: string;
    app?: string;
  };
  const navigate = useNavigate();
  const { projects, scopeNodes, scopeTreeStatus } = useContextChain();
  const [entries, setEntries] = useState<LastProjectEntry[] | null>(null);
  const initialResumeHandled = useRef(false);
  const lastPersisted = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getLastScope()
      .then((value) => {
        if (cancelled) return;
        lastPersisted.current = value;
        setEntries(parseLastProjects(value));
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => { cancelled = true; };
  }, []);

  const routeProjectId = params.project
    ?? (params.node && scopeNodes.find(
      (node) => node.id === params.node && node.node_type === "project",
    ) ? params.node : undefined);

  useEffect(() => {
    if (!entries || scopeTreeStatus !== "ready" || !params.ws || !routeProjectId) return;
    const accessible = projects.some(
      (project) => project.id === routeProjectId && project.workspaceId === params.ws,
    );
    if (!accessible) return;
    const next = rememberProject(entries, params.ws, routeProjectId);
    const serialized = serializeLastProjects(next);
    if (serialized === lastPersisted.current) return;
    lastPersisted.current = serialized;
    setEntries(next);
    void putLastScope(serialized).catch(() => {
      lastPersisted.current = null;
    });
  }, [entries, params.ws, projects, routeProjectId, scopeTreeStatus]);

  useEffect(() => {
    if (initialResumeHandled.current || !entries || scopeTreeStatus !== "ready") return;
    const isWorkspaceHome = Boolean(
      params.ws
      && !params.client
      && !params.eng
      && !params.project
      && !params.node
      && !params.app,
    );
    initialResumeHandled.current = true;
    if (!isWorkspaceHome || !params.ws) return;
    const saved = entries.find((entry) => entry.workspaceId === params.ws);
    if (!saved) return;
    const accessible = projects.some(
      (project) => project.id === saved.projectId && project.workspaceId === params.ws,
    );
    if (accessible) {
      navigate({ ...navTo.nodeHome(params.ws, saved.projectId), replace: true } as never);
    }
  }, [entries, navigate, params, projects, scopeTreeStatus]);

  return null;
}
